import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MdOutlineDelete, MdOutlineEdit, MdOutlineMoreHoriz } from 'react-icons/md';
import { ActionMenu, MenuItemType, TableColumnType } from '@ui';
import { makeCellTextRenderers } from '@features/components/TableCellTextMR';
import { Tooltip } from '@ds';
import { DriverAffiliationType, DriverListFilters, Driver } from '@/api/apis/resources';
import { col, COMMON_COLUMN_PRESETS } from '../../utils';

export const BANNED_DRIVERS_LIST_FILTERS_BASE: DriverListFilters = {
  affiliationTypes: [],
};

export const FILTERS_NAMES: Record<string, string> = {
  affilation: 'Affilation',
};

export const BANNED_DRIVERS_COLUMNS: TableColumnType[] = [
  col('name', 'Banned Driver Name', {
    sortable: true,
    minWidthClassName: 'min-w-70',
  }),
  col('affiliation', 'Affilation', {
    sortable: true,
    minWidthClassName: 'min-w-70',
  }),
  col('provider', 'Provider', {
    sortable: false,
    minWidthClassName: 'min-w-70',
  }),
  col('phone', 'Phone Number', {
    sortable: false,
    minWidthClassName: 'min-w-70',
  }),
  col('comment', 'Comment', {
    sortable: false,
    minWidthClassName: 'min-w-80',
  }),
  COMMON_COLUMN_PRESETS.actions,
];

const getCreateActionsMenu = (onEdit: () => void, onDelete: () => void): MenuItemType[] => [
  {
    key: 'edit-banned-driver',
    type: 'action',
    to: 'edit-banned-driver',
    label: (
      <div className="flex items-center gap-2">
        <MdOutlineEdit size={16} />
        <span className="text-m-r">Edit</span>
      </div>
    ),
    onAction: () => onEdit(),
  },
  {
    key: 'delete-banned-driver',
    type: 'action',
    to: 'delete-banned-driver',
    label: (
      <div className="flex items-center gap-2 text-danger">
        <MdOutlineDelete size={16} />
        <span className="text-m-r">Delete</span>
      </div>
    ),
    onAction: () => onDelete(),
  },
];

const getEditActionsMenu = (onEdit: () => void): MenuItemType[] => [
  {
    key: 'edit-banned-driver',
    type: 'action',
    to: 'edit-banned-driver',
    label: (
      <div className="flex items-center gap-2">
        <MdOutlineEdit size={16} />
        <span className="text-m-r">Edit</span>
      </div>
    ),
    onAction: () => onEdit(),
  },
];

const getAffiliationNames = (affiliation: DriverAffiliationType | null) => {
  if (!affiliation) {
    return '-';
  }

  switch (affiliation) {
    case DriverAffiliationType.TMDX:
      return 'TMDX Affiliate';
    case DriverAffiliationType.TXCenterProvider:
      return 'Tx Center Provider';
    default:
      return '-';
  }
};

const renderCommentCell = (comment?: string | null) => {
  if (!comment) {
    return '-';
  }

  return (
    <Tooltip
      placement="top-start"
      content={<div className="max-w-140 whitespace-normal wrap-break-word">{comment}</div>}
    >
      <span className="block w-80 overflow-hidden text-ellipsis whitespace-nowrap text-m-r">{comment}</span>
    </Tooltip>
  );
};

export const getRenderDriversTableCell =
  (
    onEdit: (bannedDriverId: string) => void,
    onDelete: (id: string, data: Driver) => void,
    hasCreatePermissions: boolean,
  ) =>
  (bannedDriver: Driver, columnKey: Key): ReactNode => {
    const { id, firstName, lastName, phone, comment, affiliationType, provider, companyName } = bannedDriver;

    const onBannedDriverEdit = () => onEdit(id);
    const onBannedDriverDelete = () => onDelete(id, bannedDriver);

    const { text } = makeCellTextRenderers();

    const ACTIONS_MENU_CREATE = getCreateActionsMenu(onBannedDriverEdit, onBannedDriverDelete);
    const ACTIONS_MENU_EDIT = getEditActionsMenu(onBannedDriverEdit);

    switch (columnKey) {
      case 'name':
        return (
          <Link to={`/admin/configuration/banned_drivers/${id}`}>
            <span className="text-m-r">
              {firstName} {lastName}
            </span>
          </Link>
        );

      case 'affiliation':
        return text(getAffiliationNames(affiliationType));

      case 'provider':
        return text(companyName || provider?.name || '-');

      case 'phone':
        return text(phone || '-');

      case 'comment':
        return renderCommentCell(comment);

      case 'actions':
        return (
          <ActionMenu
            placement="left-start"
            items={hasCreatePermissions ? ACTIONS_MENU_CREATE : ACTIONS_MENU_EDIT}
            MenuTrigger={
              <div>
                <MdOutlineMoreHoriz size={20} />
              </div>
            }
            label="Banned drivers actions menu"
          />
        );

      default:
        return null;
    }
  };
